import React, { useState, useEffect, useCallback } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";
import { Loading } from "../components/Loading";
import { ActivityBadge, ReferenceBadge, HourType } from "../components/Badges";
import { SmartProjectSelector } from "../components/SmartProjectSelector";
import { useToast } from "../contexts/ToastContext";
import { useApiAuth } from "../hooks/useApiAuth";
import { useActivitiesContext } from "../contexts/ActivitiesContext";
import { useNotifications } from "../contexts/NotificationsContext";
import {
  apiService,
  TimesheetWeek,
  TimesheetEntry,
  TimesheetSaveEntry,
  TimesheetSaveRequest,
} from "../services/apiService";
import {
  getCurrentWeek,
  parseWeekKey,
  getWeekInfo,
  formatWeekTooltip,
} from "../utils/weekUtils";

interface TimesheetWeekData {
  week: TimesheetWeek;
  entries: TimesheetEntry[];
  editedEntries: Map<number, TimesheetSaveEntry>;
  isLoading: boolean;
  hasChanges: boolean;
}

export const TimesheetPage: React.FC = () => {
  const [todoWeeks, setTodoWeeks] = useState<TimesheetWeek[]>([]);
  const [completedWeeks, setCompletedWeeks] = useState<TimesheetWeek[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<TimesheetWeek | null>(null);
  const [weekData, setWeekData] = useState<Map<string, TimesheetWeekData>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [savingWeeks, setSavingWeeks] = useState<Set<string>>(new Set());
  const [selectedProject, setSelectedProject] = useState<string>("");

  const toast = useToast();
  const { isApiReady } = useApiAuth();
  const { activities } = useActivitiesContext();
  const { refetch: refetchNotifications } = useNotifications();

  // Calculate overdue weeks (weeks that are before current week and not completed)
  const currentWeekNumber = getCurrentWeek();
  const currentYear = new Date().getFullYear();
  const currentWeekId = parseInt(
    `${currentYear}${currentWeekNumber.toString().padStart(2, "0")}`,
  );

  const loadTimesheetWeeks = useCallback(async () => {
    try {
      setLoading(true);

      // Load both TODO and completed weeks
      const [todoResponse, completedResponse] = await Promise.all([
        apiService.getTimesheetWeeks(false), // TODO weeks
        apiService.getTimesheetWeeks(true), // Completed weeks
      ]);

      if (todoResponse.success && todoResponse.data) {
        setTodoWeeks(todoResponse.data);
      }

      if (completedResponse.success && completedResponse.data) {
        setCompletedWeeks(completedResponse.data.slice(0, 10)); // Show latest 10 completed weeks
      }
    } catch (error) {
      console.error("Failed to load timesheet weeks:", error);
      toast.showError("Error", "Failed to load timesheet data");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadWeekData = useCallback(
    async (week: TimesheetWeek) => {
      const weekId = String(week.week_id);

      // Skip if already loading or loaded
      if (weekData.has(weekId)) return;

      try {
        // Set loading state
        setWeekData(
          (prev) =>
            new Map(
              prev.set(weekId, {
                week,
                entries: [],
                editedEntries: new Map(),
                isLoading: true,
                hasChanges: false,
              }),
            ),
        );

        const response = await apiService.getTimesheetWeek(Number(weekId));

        if (response.success && response.data) {
          const editedEntries = new Map<number, TimesheetSaveEntry>();

          // Initialize edited entries with current values
          response.data.forEach((entry) => {
            editedEntries.set(entry.activity_id, {
              actuals: entry.actuals || 0,
              activity_id: entry.activity_id,
              comment: entry.comment,
              billable: entry.billable ?? entry.hour_type === "billable",
            });
          });

          setWeekData(
            (prev) =>
              new Map(
                prev.set(weekId, {
                  week,
                  entries: response.data!,
                  editedEntries,
                  isLoading: false,
                  hasChanges: false,
                }),
              ),
          );
        }
      } catch (error) {
        console.error("Failed to load week data:", error);
        toast.showError(
          "Error",
          `Failed to load timesheet for week ${week.week}`,
        );

        // Remove loading state on error
        setWeekData((prev) => {
          const newMap = new Map(prev);
          newMap.delete(weekId);
          return newMap;
        });
      }
    },
    [weekData, toast],
  );

  // Load timesheet weeks on component mount and set default selected week
  useEffect(() => {
    if (!isApiReady) return;

    loadTimesheetWeeks();
  }, [isApiReady, loadTimesheetWeeks]);

  // Auto-select oldest TODO week when data loads
  useEffect(() => {
    if (todoWeeks.length > 0 && !selectedWeek) {
      const oldestWeek = todoWeeks.reduce((oldest, current) =>
        current.week_id < oldest.week_id ? current : oldest,
      );
      setSelectedWeek(oldestWeek);
    }
  }, [todoWeeks, selectedWeek]);

  // Load week data when selected week changes
  useEffect(() => {
    if (selectedWeek) {
      loadWeekData(selectedWeek);
    }
  }, [selectedWeek, loadWeekData]);

  const updateEntry = (
    weekId: string,
    activityId: number,
    field: keyof TimesheetSaveEntry,
    value: any,
  ) => {
    setWeekData((prev) => {
      const newMap = new Map(prev);
      const weekDataEntry = newMap.get(weekId);

      if (weekDataEntry) {
        const newEditedEntries = new Map(weekDataEntry.editedEntries);
        const currentEntry = newEditedEntries.get(activityId);

        if (currentEntry) {
          newEditedEntries.set(activityId, {
            ...currentEntry,
            [field]: value,
          });

          newMap.set(weekId, {
            ...weekDataEntry,
            editedEntries: newEditedEntries,
            hasChanges: true,
          });
        }
      }

      return newMap;
    });
  };

  const saveTimesheet = async (weekId: string, complete: boolean = false) => {
    const weekDataEntry = weekData.get(weekId);
    if (!weekDataEntry) return;

    try {
      setSavingWeeks((prev) => new Set(prev.add(weekId)));

      const saveData: TimesheetSaveRequest = {
        week: parseInt(weekId),
        complete,
        data: Array.from(weekDataEntry.editedEntries.values()),
      };

      const response = await apiService.saveTimesheet(saveData);

      if (response.success) {
        toast.showSuccess(
          complete ? "Timesheet Completed" : "Timesheet Saved",
          complete
            ? `Week ${weekDataEntry.week.week} timesheet has been completed`
            : `Week ${weekDataEntry.week.week} timesheet has been saved`,
        );

        // Mark as saved
        setWeekData(
          (prev) =>
            new Map(
              prev.set(weekId, {
                ...weekDataEntry,
                hasChanges: false,
              }),
            ),
        );

        // If completed, refresh the weeks data and open-timesheet badge
        if (complete) {
          await loadTimesheetWeeks();
          await refetchNotifications();

          // Auto-select the first available TODO timesheet
          const updatedTodoResponse = await apiService.getTimesheetWeeks(false);
          if (
            updatedTodoResponse.success &&
            updatedTodoResponse.data &&
            updatedTodoResponse.data.length > 0
          ) {
            const oldestTodoWeek = updatedTodoResponse.data.reduce(
              (oldest, current) =>
                current.week_id < oldest.week_id ? current : oldest,
            );
            setSelectedWeek(oldestTodoWeek);
          } else {
            // No more TODO weeks, clear selection
            setSelectedWeek(null);
          }
        }
      } else {
        toast.showError("Error", "Failed to save timesheet");
      }
    } catch (error) {
      console.error("Failed to save timesheet:", error);
      toast.showError("Error", "Failed to save timesheet");
    } finally {
      setSavingWeeks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(weekId);
        return newSet;
      });
    }
  };

  const formatHours = (hours: number | null | undefined): string => {
    return (hours || 0).toFixed(2);
  };

  const getTotalHours = (
    entries: TimesheetSaveEntry[],
  ): { planned: number; actual: number } => {
    const weekDataEntry = Array.from(weekData.values()).find(
      (data) =>
        data.editedEntries.size > 0 &&
        Array.from(data.editedEntries.values())[0] === entries[0],
    );

    if (!weekDataEntry) return { planned: 0, actual: 0 };

    const planned = weekDataEntry.entries.reduce(
      (sum, entry) => sum + (entry.planned || 0),
      0,
    );
    const actual = entries.reduce(
      (sum, entry) => sum + (entry.actuals || 0),
      0,
    );

    return { planned, actual };
  };

  const addProjectToTimesheet = () => {
    if (!selectedProject || !selectedWeek) return;

    // Find the selected project object by ID from activities
    const selectedActivity = activities.find(
      (activity) => activity.id.toString() === selectedProject,
    );
    if (
      !selectedActivity ||
      !selectedActivity.project ||
      !selectedActivity.project.client
    )
      return;

    // Transform activity to project data
    const selectedProjectData = {
      id: selectedActivity.id.toString(),
      activityId: selectedActivity.id,
      name: selectedActivity.name,
      activity: selectedActivity.name,
      client: selectedActivity.project!.client.name,
      project: selectedActivity.project!.name,
      hourType: selectedActivity.hour_type as HourType,
      reference: selectedActivity.project!.reference,
    };

    const weekId = String(selectedWeek.week_id);
    const weekDataEntry = weekData.get(weekId);

    if (!weekDataEntry) return;

    // Check if project already exists
    const existingEntry = weekDataEntry.entries.find(
      (entry) => entry.activity_id === selectedProjectData.activityId,
    );

    if (existingEntry) {
      toast.showError("Error", "This project is already in the timesheet");
      return;
    }

    // Add new entry to edited entries
    const newEntry: TimesheetSaveEntry = {
      actuals: 0,
      activity_id: selectedProjectData.activityId,
      comment: "",
      billable: selectedProjectData.hourType === "billable",
    };

    // Add to weekData entries and editedEntries
    setWeekData((prev) => {
      const newMap = new Map(prev);
      const currentWeekData = newMap.get(weekId);

      if (currentWeekData) {
        const newTimesheetEntry: TimesheetEntry = {
          activity: selectedProjectData.activity,
          activity_id: selectedProjectData.activityId,
          actuals: 0,
          client: selectedProjectData.client,
          comment: "",
          hour_type: selectedProjectData.hourType,
          planned: 0,
          project: selectedProjectData.project,
          reference: selectedProjectData.reference,
          billable: selectedProjectData.hourType === "billable",
        };

        const updatedEditedEntries = new Map(currentWeekData.editedEntries);
        updatedEditedEntries.set(selectedProjectData.activityId, newEntry);

        newMap.set(weekId, {
          ...currentWeekData,
          entries: [...currentWeekData.entries, newTimesheetEntry],
          editedEntries: updatedEditedEntries,
          hasChanges: true,
        });
      }

      return newMap;
    });

    setSelectedProject("");
    toast.showSuccess("Success", "Project added to timesheet");
  };

  if (loading) {
  }

  const renderTimesheetTable = (
    weekId: string,
    weekDataEntry: TimesheetWeekData,
  ) => {
    const entries = Array.from(weekDataEntry.editedEntries.values());
    const { planned, actual } = getTotalHours(entries);

    const hoursBodyTemplate = (entry: TimesheetSaveEntry) => {
      return (
        <InputNumber
          value={entry.actuals}
          onValueChange={(e) =>
            updateEntry(weekId, entry.activity_id, "actuals", e.value || 0)
          }
          onFocus={(e) => {
            // More reliable text selection - use requestAnimationFrame for better timing
            requestAnimationFrame(() => {
              const input = e.target as HTMLInputElement;
              if (input && input.select) {
                input.select();
              }
            });
          }}
          onClick={(e) => {
            // Also handle click event to ensure selection on click
            const input = e.target as HTMLInputElement;
            if (input && input.select) {
              input.select();
            }
          }}
          mode="decimal"
          minFractionDigits={2}
          maxFractionDigits={2}
          step={0.25}
          min={0}
          max={168} // Max hours in a week
          disabled={weekDataEntry.week.complete}
          className="w-full"
        />
      );
    };

    const commentBodyTemplate = (entry: TimesheetSaveEntry) => {
      return (
        <InputTextarea
          value={entry.comment}
          onChange={(e) =>
            updateEntry(weekId, entry.activity_id, "comment", e.target.value)
          }
          rows={1}
          disabled={weekDataEntry.week.complete}
          className="w-full"
          placeholder="Add comments..."
          autoResize
        />
      );
    };

    const projectBodyTemplate = (entry: TimesheetSaveEntry) => {
      const originalEntry = weekDataEntry.entries.find(
        (e) => e.activity_id === entry.activity_id,
      );
      if (!originalEntry) return null;

      return (
        <div>
          <div className="font-semibold">
            {originalEntry.client} - {originalEntry.project}
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            <ActivityBadge
              value={originalEntry.activity}
              hourType={originalEntry.hour_type as any}
              size="normal"
            />
            <ReferenceBadge value={originalEntry.reference} size="normal" />
          </div>
        </div>
      );
    };

    const plannedBodyTemplate = (entry: TimesheetSaveEntry) => {
      const originalEntry = weekDataEntry.entries.find(
        (e) => e.activity_id === entry.activity_id,
      );
      return formatHours(originalEntry?.planned || 0);
    };

    return (
      <div>
        <DataTable
          value={entries}
          responsiveLayout="scroll"
          className="p-datatable-sm"
          dataKey="activity_id"
        >
          <Column
            field="project"
            header="Project"
            body={projectBodyTemplate}
            style={{ minWidth: "250px" }}
            footer="Totals:"
            footerStyle={{ fontWeight: "bold" }}
          />
          <Column
            field="planned"
            header="Planned"
            body={plannedBodyTemplate}
            style={{ width: "100px", textAlign: "center" }}
            footer={formatHours(planned)}
            footerStyle={{ fontWeight: "bold", textAlign: "center" }}
          />
          <Column
            field="actuals"
            header="Actual Hours"
            body={hoursBodyTemplate}
            style={{ width: "150px" }}
            footer={formatHours(actual)}
            footerStyle={{
              fontWeight: "bold",
              textAlign: "center",
            }}
          />
          <Column
            field="comment"
            header="Comments"
            body={commentBodyTemplate}
            style={{ minWidth: "200px" }}
            footer=""
          />
        </DataTable>
      </div>
    );
  };

  if (loading) {
    return <Loading message="Loading timesheets..." />;
  }

  const selectedWeekData = selectedWeek
    ? weekData.get(String(selectedWeek.week_id))
    : null;
  const isSaving = selectedWeek
    ? savingWeeks.has(String(selectedWeek.week_id))
    : false;

  return (
    <div className="page-container">
      <div className="grid">
        {/* Left sidebar with week lists */}
        <div className="col-12 md:col-2">
          <div className="mb-4">
            {renderWeeksList(todoWeeks, `TODO (${todoWeeks.length})`)}
          </div>
          <div>
            {renderWeeksList(
              completedWeeks.slice(0, 10),
              `Done (${Math.min(completedWeeks.length, 10)})`,
              true,
            )}
          </div>
        </div>

        {/* Main timesheet area */}
        <div className="col-12 md:col-10">
          {selectedWeek ? (
            <Card
              title={`Week ${selectedWeek.week} (${selectedWeek.year}) Timesheet`}
              subTitle={(() => {
                const { year, weekNumber } = parseWeekKey(
                  String(selectedWeek.week_id),
                );
                const weekInfo = getWeekInfo(year, weekNumber);
                return formatWeekTooltip(weekInfo);
              })()}
            >
              {!selectedWeekData ? (
                <div className="text-center py-8">
                  <ProgressBar mode="indeterminate" style={{ height: "4px" }} />
                  <p className="mt-2 text-500">Loading timesheet...</p>
                </div>
              ) : selectedWeekData.isLoading ? (
                <div className="text-center py-8">
                  <ProgressBar mode="indeterminate" style={{ height: "4px" }} />
                  <p className="mt-2 text-500">Loading timesheet...</p>
                </div>
              ) : (
                <div>
                  {renderTimesheetTable(
                    String(selectedWeek.week_id),
                    selectedWeekData,
                  )}

                  {/* Add Project Section */}
                  {!selectedWeek.complete && (
                    <div className="mt-4 p-3 border border-gray-200 rounded">
                      <div className="flex gap-2 align-items-end">
                        <div className="flex-1">
                          <SmartProjectSelector
                            value={selectedProject}
                            onChange={setSelectedProject}
                            label="Add Project"
                            placeholder="Choose a project to add..."
                            required={false}
                          />
                        </div>
                        <div className="pb-4">
                          <Button
                            label="Add"
                            icon="pi pi-check"
                            size="small"
                            disabled={!selectedProject}
                            onClick={addProjectToTimesheet}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!selectedWeek.complete && (
                    <div className="flex gap-2 justify-content-end mt-4">
                      <Button
                        label="Save"
                        icon="pi pi-save"
                        size="small"
                        outlined
                        disabled={!selectedWeekData.hasChanges || isSaving}
                        loading={isSaving}
                        onClick={() =>
                          saveTimesheet(String(selectedWeek.week_id), false)
                        }
                      />
                      <Button
                        label="Save & Complete"
                        icon="pi pi-check"
                        size="small"
                        severity="success"
                        disabled={isSaving}
                        loading={isSaving}
                        onClick={() =>
                          saveTimesheet(String(selectedWeek.week_id), true)
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </Card>
          ) : (
            <Card title="Select a Timesheet">
              <div className="text-center py-8">
                <i className="pi pi-clock text-6xl text-400 mb-4"></i>
                <p className="text-500 text-lg">
                  Select a week from the list to view and edit your timesheet
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );

  // Helper function to render week lists
  function renderWeeksList(
    weeks: TimesheetWeek[],
    title: string,
    isDone: boolean = false,
  ) {
    return (
      <Card title={title} className="h-full">
        {weeks.length === 0 ? (
          <p className="text-center text-500 py-4">
            No {isDone ? "completed" : "open"} timesheets found.
          </p>
        ) : (
          <div className="space-y-2">
            {weeks.map((week) => {
              const isOverdue = !isDone && week.week_id < currentWeekId;
              const isSelected = selectedWeek?.week_id === week.week_id;

              return (
                <div
                  key={week.week_id}
                  className={`p-3 border border-gray-200 rounded cursor-pointer transition-colors
                    ${
                      isSelected
                        ? "bg-blue-50 border-blue-300"
                        : "hover:bg-gray-50"
                    }
                    ${isOverdue ? "border-red-300 bg-red-50" : ""}
                  `}
                  onClick={() => setSelectedWeek(week)}
                >
                  <div className="flex justify-content-between align-items-center">
                    <div>
                      <span className="font-semibold">
                        Week {week.week} ({week.year})
                      </span>
                      {isOverdue && (
                        <Badge
                          value="OVERDUE"
                          severity="danger"
                          className="ml-2"
                        />
                      )}
                      {isDone && (
                        <Tag
                          value="Completed"
                          severity="success"
                          icon="pi pi-check"
                          className="ml-2"
                        />
                      )}
                    </div>
                    {isSelected && (
                      <i className="pi pi-angle-right text-blue-500"></i>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  }
};
